import { motion } from "framer-motion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SectionHead, FieldLabel, FieldError } from "@/components/inquiry/inquiryFields";

const TIMELINES = [
    "ASAP (rush)",
    "2 – 3 weeks",
    "1 – 2 months",
    "2 – 4 months",
    "Flexible",
];

/**
 * StepTimeline — Package journey timeline picker.
 *
 * Package Inquiry rationale
 * -------------------------
 * When a client selects a predefined package, they have already committed to an
 * investment level. Asking for a Budget Range on top of that is redundant and
 * can create contradictions (e.g. a $15,000 package paired with a $5–10k budget).
 * So the Package Journey uses this Timeline-only step, and the selected package
 * remains the source of investment truth.
 *
 * Custom Project Inquiry still uses `StepBudget` (budget + timeline together)
 * because those clients don't have a predefined price anchor.
 */
export const StepTimeline = ({ data, onField, errors }) => (
    <section data-testid="step-timeline">
        <SectionHead
            eyebrow="Step 06 · Timeline"
            title="When do you need this?"
            description="Your selected package already reflects your investment. Just let us know your ideal timeline so we can plan resourcing on our side."
        />

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
                <FieldLabel htmlFor="timeline-select">Expected Timeline</FieldLabel>
                <Select value={data.expected_timeline || undefined} onValueChange={(v) => onField("expected_timeline", v)}>
                    <SelectTrigger
                        id="timeline-select"
                        className="h-12 rounded-xl pg-input"
                        style={{ background: "var(--pg-bg)" }}
                        data-testid="timeline-select"
                    >
                        <SelectValue placeholder="When do you need this?" />
                    </SelectTrigger>
                    <SelectContent>
                        {TIMELINES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                </Select>
                <FieldError error={errors?.expected_timeline} />
            </div>
        </div>

        <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="mt-6 text-[11px] leading-relaxed"
            style={{ color: "var(--pg-fg-muted)" }}
        >
            Rush timelines (ASAP) may add a small priority-delivery fee — we&apos;ll confirm scope + timeline together after reviewing your brief.
        </motion.p>
    </section>
);

export default StepTimeline;
